import {
	BankOperationType,
	BankTransactionCategory,
	ClientError,
	ClientErrorCode,
	IBankPinAttempt,
	IBankStats,
	Job,
	WalletType,
} from '~/shared';

import { IObservable, Subject } from '../../utils';
import { IBankService, IBankTransaction } from './bankServiceTypes';

export class BankServiceStub implements IBankService {
	private readonly wallets: {
		[key in WalletType]: Subject<number>;
	} = {
		[WalletType.Cash]: new Subject(50000),
		[WalletType.Bank]: new Subject(45000000),
		[WalletType.Donate]: new Subject(2500),
	};

	private readonly walletDisplayedDiffs: {
		[key in WalletType]: Subject<number>;
	} = {
		[WalletType.Cash]: new Subject(0),
		[WalletType.Bank]: new Subject(0),
		[WalletType.Donate]: new Subject(0),
	};

	private readonly walletPendingDiffs: {
		[key in WalletType]: number;
	} = {
		[WalletType.Cash]: 0,
		[WalletType.Bank]: 0,
		[WalletType.Donate]: 0,
	};

	private readonly walletPrevValues: {
		[key in WalletType]: number;
	} = {
		[WalletType.Cash]: 50000,
		[WalletType.Bank]: 45000000,
		[WalletType.Donate]: 2500,
	};

	private readonly cardNumber = new Subject<string>('4276180000000000');
	private readonly isPinCodeSet = new Subject<boolean>(true);
	private readonly pinCode = new Subject<string>('1234');
	private readonly banExpiresAt = new Subject<number | null>(null);
	private readonly errorCount = new Subject<number>(0);
	private readonly pinCodeResetResendAt = new Subject<number | null>(null);
	private readonly pinCodeResetEmailCode = '1234';
	private readonly pinCodeResetToken = 'stub-pin-reset-token';
	private readonly isBankAppAvailable = new Subject<boolean>(true);
	private readonly pinCodeLength = new Subject<number>(4);
	private readonly operationTypesFees: {
		[key in BankOperationType]: Subject<number>;
	} = {
		[BankOperationType.DEPOSIT]: new Subject(0.05),
		[BankOperationType.WITHDRAW]: new Subject(0.05),
		[BankOperationType.TRANSFER]: new Subject(0.05),
		[BankOperationType.TAX]: new Subject(0),
	};

	private readonly stats = new Subject<IBankStats>({
		earned: 100000,
		spent: 27000,
		registeredAt: Date.now(),
		cardExpiresAt: Date.now() + 1000 * 60 * 60 * 24 * 365,
	});

	private readonly transactions = new Subject<IBankTransaction[]>([
		{
			amount: 150000,
			wallet: WalletType.Cash,
			createdAt: new Date(2020, 1, 1),
			description: 'Стартовые деньги',
			category: BankTransactionCategory.BANK,
		},
		{
			amount: -5000,
			wallet: WalletType.Cash,
			createdAt: new Date(2020, 1, 2, 12, 30),
			description: 'Покупка топлива',
			category: BankTransactionCategory.VEHICLE,
		},
		{
			amount: 50000,
			wallet: WalletType.Cash,
			createdAt: new Date(2020, 1, 2, 12, 35),
			description: 'Перевод от игрока',
			category: BankTransactionCategory.TRANSFER,
		},
		{
			amount: -20000,
			wallet: WalletType.Cash,
			createdAt: new Date(2020, 1, 2, 12, 40),
			description: 'Перевод игроку',
			category: BankTransactionCategory.TRANSFER,
		},
		{
			amount: -1234,
			wallet: WalletType.Bank,
			createdAt: new Date(2020, 1, 2, 12, 45),
			description: 'Покупка в магазине',
			category: BankTransactionCategory.SHOP,
		},
		{
			amount: -12345,
			wallet: WalletType.Bank,
			createdAt: new Date(2020, 1, 2, 12, 50),
			description: 'Покупка в магазине',
			category: BankTransactionCategory.SHOP,
		},
		{
			amount: 12345,
			wallet: WalletType.Bank,
			createdAt: new Date(2020, 1, 2, 12, 51),
			description: 'Банкомат #123',
			category: BankTransactionCategory.DEPOSIT,
		},
		{
			amount: -12345,
			wallet: WalletType.Bank,
			createdAt: new Date(2020, 1, 2, 12, 52),
			description: 'Банкомат #124',
			category: BankTransactionCategory.WITHDRAW,
		},
		{
			amount: -12345,
			wallet: WalletType.Bank,
			createdAt: new Date(2020, 1, 2, 12, 53),
			description: 'Покупка в магазине',
			category: BankTransactionCategory.SHOP,
		},
		{
			amount: -12345,
			wallet: WalletType.Bank,
			createdAt: new Date(2020, 1, 2, 12, 54),
			description: 'Покупка в магазине',
			category: BankTransactionCategory.SHOP,
		},
		{
			amount: -12345,
			wallet: WalletType.Bank,
			createdAt: new Date(2020, 1, 2, 12, 55),
			description: 'Покупка в магазине',
			category: BankTransactionCategory.SHOP,
		},
		{
			amount: -12345,
			wallet: WalletType.Bank,
			createdAt: new Date(2020, 1, 2, 12, 56),
			description: 'Покупка в магазине',
			category: BankTransactionCategory.SHOP,
		},
	]);

	constructor() {
		for (const walletType of Object.values(WalletType)) {
			this.wallets[walletType].subscribe((newValue) => {
				const prevValue = this.walletPrevValues[walletType];
				const diff = newValue - prevValue;

				if (diff === 0) {
					return;
				}

				const currentDisplayed =
					this.walletDisplayedDiffs[walletType].getValue();
				if (currentDisplayed !== 0) {
					this.walletPendingDiffs[walletType] += diff;
				} else {
					this.walletDisplayedDiffs[walletType].next(currentDisplayed + diff);
				}

				this.walletPrevValues[walletType] = newValue;
			});
		}

		setInterval(() => {
			for (const wallet of Object.values(this.wallets)) {
				let value = wallet.getValue();
				if (Math.random() > 0.5) {
					value += Math.round(Math.random() * 10) * 1000;
				} else {
					value -= Math.round(Math.random() * 10) * 1000;
				}

				if (value < 0) {
					value = 0;
				}

				wallet.next(value);
			}
		}, 10000);

		this.transactions.next(
			this.transactions
				.getValue()
				.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
		);
	}

	public async fetch(): Promise<void> {
		console.log('fetch');
	}

	public getBalance(wallet: WalletType): IObservable<number> {
		return this.wallets[wallet];
	}

	public getCardNumber(): IObservable<string> {
		return this.cardNumber;
	}

	public async exchange(
		fromWallet: WalletType,
		toWallet: WalletType,
		amount: number,
	): Promise<void> {
		console.log('exchange', {
			fromWallet,
			toWallet,
			amount,
		});

		if (amount <= 0) {
			throw new Error('Amount must be greater than 0');
		}

		const transferFee = this.getTransferFee(
			fromWallet === WalletType.Cash
				? BankOperationType.DEPOSIT
				: BankOperationType.WITHDRAW,
		).getValue();
		const fromBalance = this.wallets[fromWallet].getValue();
		if (fromBalance < amount) {
			throw new ClientError(ClientErrorCode.NotEnoughMoney);
		}

		const toBalance = this.wallets[toWallet].getValue();
		const exchangedBalance = amount * transferFee;

		await new Promise((resolve) => setTimeout(resolve, 1000));

		this.wallets[fromWallet].next(fromBalance - amount);
		this.wallets[toWallet].next(toBalance + exchangedBalance);
	}

	public async transfer(cardNumber: string, amount: number): Promise<void> {
		console.log('transfer', {
			cardNumber,
			amount,
		});

		const bankBalance = this.wallets[WalletType.Bank].getValue();
		if (bankBalance < amount) {
			throw new ClientError(ClientErrorCode.NotEnoughMoney);
		}

		await new Promise((resolve) => setTimeout(resolve, 1000));

		this.wallets[WalletType.Bank].next(bankBalance - amount);
	}

	public getTransactionHistory(): IObservable<IBankTransaction[]> {
		return this.transactions;
	}

	public async close(): Promise<void> {
		console.log('close bank');
	}

	public getTransferFee(type: BankOperationType): IObservable<number> {
		return this.operationTypesFees[type];
	}

	public getBankStats(): IObservable<IBankStats> {
		return this.stats;
	}

	public getIsPinCodeSet(): IObservable<boolean> {
		return this.isPinCodeSet;
	}

	public async setPinCode(pinCode: string): Promise<void> {
		this.pinCode.next(pinCode);
		this.isPinCodeSet.next(true);
	}

	public async checkPinCode(pinCode: string): Promise<IBankPinAttempt> {
		const isCorrect = pinCode === this.pinCode.getValue();
		if (!isCorrect) {
			this.errorCount.next(this.errorCount.getValue() + 1);
			if (this.errorCount.getValue() >= 3) {
				this.banExpiresAt.next(Date.now() + 10000);
				setTimeout(() => {
					this.errorCount.next(0);
					this.banExpiresAt.next(null);
				}, 10000);
			}
		}

		return await new Promise((resolve) => {
			setTimeout(() => {
				resolve({
					isSucceed: isCorrect,
					attemptsLeft: 3 - this.errorCount.getValue(),
				});
			}, 200);
		});
	}

	public getBanExpiresAt(): IObservable<number | null> {
		return this.banExpiresAt;
	}

	public getIsBankAppAvailable(): IObservable<boolean> {
		return this.isBankAppAvailable;
	}

	public getPinCodeLength(): IObservable<number> {
		return this.pinCodeLength;
	}

	public async getCardOwner(cardNumber: string): Promise<string | null> {
		if (cardNumber.charAt(0) !== '4') {
			return await new Promise((resolve) => {
				setTimeout(() => {
					resolve(null);
				}, 150);
			});
		}
		return new Promise((resolve) => {
			setTimeout(() => {
				resolve('Dmitrii Stepanov');
			}, 150);
		});
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async getDailyJobProfit(jobId: Job): Promise<number> {
		return 0;
	}

	public getBalanceDiff(wallet: WalletType): IObservable<number> {
		return this.walletDisplayedDiffs[wallet];
	}

	public commitBalanceDiff(wallet: WalletType): number {
		return this.walletDisplayedDiffs[wallet].getValue();
	}

	public finishDisplayingBalanceDiff(wallet: WalletType): void {
		this.walletDisplayedDiffs[wallet].next(0);
		const pending = this.walletPendingDiffs[wallet];
		if (pending !== 0) {
			this.walletPendingDiffs[wallet] = 0;
			this.walletDisplayedDiffs[wallet].next(pending);
		}
	}

	public async requestPinCodeReset(): Promise<{ resendAvailableAt: number }> {
		await new Promise((resolve) => setTimeout(resolve, 300));
		const resendAvailableAt = Date.now() + 120 * 1000;
		this.pinCodeResetResendAt.next(resendAvailableAt);
		console.log(
			`[stub] bank pin reset code sent. Use code "${this.pinCodeResetEmailCode}"`,
		);
		return { resendAvailableAt };
	}

	public async confirmPinCodeResetCode(
		code: string,
	): Promise<{ token: string }> {
		await new Promise((resolve) => setTimeout(resolve, 300));
		if (code !== this.pinCodeResetEmailCode) {
			throw new Error('Неверный код подтверждения');
		}
		return { token: this.pinCodeResetToken };
	}

	public async resetPinCode(token: string, newPinCode: string): Promise<void> {
		await new Promise((resolve) => setTimeout(resolve, 300));
		if (token !== this.pinCodeResetToken) {
			throw new Error('Неверный токен сброса пин-кода');
		}
		this.pinCode.next(newPinCode);
		this.isPinCodeSet.next(true);
		this.errorCount.next(0);
		this.banExpiresAt.next(null);
		this.pinCodeResetResendAt.next(null);
	}

	public getPinCodeResetResendAt(): IObservable<number | null> {
		return this.pinCodeResetResendAt;
	}
}
